import React, { useState, useEffect, useMemo, useRef } from 'react'

/**
 * COMPONENTE: DANCE CAM OVERLAY (VISUAL CHILD)
 * ------------------------------------------------------------------
 * Renderiza el escenario de fiesta "Báilele" utilizando animaciones
 * CSS3 optimizadas para la GPU (tarjeta gráfica), evitando Canvas.
 * ------------------------------------------------------------------
 */
const Dance = ({ config, activeDancers = [], fiestaMode = false, manualExplodeTrigger = 0 }) => {
    const {
        titleText,
        visible,
        frameStyle, // 'neon-classic', 'stadium-visor', 'cyber-glitch'
        eqEffect, // 'suave', 'agresivo', 'estereo', 'apagado'
        eqGradientStart,
        eqGradientEnd,
        eqGlow,
        floatingShape, // 'notes-stars', 'soccer-balls', 'trophies-stars', 'all'
        shapeColor,
        shapeGlow,
        shapeOpacity,
        shapeBorderWidth,
        enableDancers,
        fogColor,
        fogSpeed,
        bpm,
        scale,
        titleSize,
        offsetX,
        offsetY,
        frameZoom
    } = config;

    // Helper para convertir hex a rgba
    const hexToRgba = (hex, alpha = 0.4) => {
        if (!hex) return `rgba(0, 255, 255, ${alpha})`;
        const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
        return result 
            ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`
            : `rgba(0, 255, 255, ${alpha})`;
    };

    const neonPrimary = fiestaMode ? '#ff00ff' : (eqGradientStart || '#00ffff');
    const neonSecondary = fiestaMode ? '#ffff00' : (eqGradientEnd || '#ff00ff');
    const glowColorRGB = hexToRgba(fiestaMode ? '#ff00ff' : (shapeColor || '#00ffff'), 0.5);
    const shapeGlowVal = `${shapeGlow || 15}px`;

    // Sincronizar el cálculo de la duración de la animación con los BPM
    const beatDuration = useMemo(() => {
        const bps = (bpm || 120) / 60;
        return 1 / bps; // Duración de un golpe en segundos
    }, [bpm]);

    const speedMultiplier = fiestaMode ? 0.25 : 1.0;
    const animationSpeed = `${beatDuration * speedMultiplier}s`;

    // Clases del contenedor principal
    const overlayClasses = `dance-overlay-container ${visible ? 'reveal' : 'hide-overlay'} style-${frameStyle} ${fiestaMode ? 'fiesta-mode' : ''}`;

    // Estilos dinámicos inyectados
    const cssVariablesStyle = {
        '--neon-primary': neonPrimary,
        '--neon-secondary': neonSecondary,
        '--neon-glow-val': `${eqGlow || 20}px`,
        '--shape-color': shapeColor || '#00ffff',
        '--shape-glow': shapeGlowVal,
        '--shape-glow-rgb': glowColorRGB,
        '--shape-opacity': shapeOpacity ?? 0.3,
        '--shape-border-width': `${shapeBorderWidth || 2}px`,
        '--fog-color': fogColor || '#250022',
        '--fog-speed': `${fogSpeed || 4.0}s`,
        '--dance-scale': scale || 1.0,
        '--title-size': `${titleSize || 8.0}rem`,
        '--offset-x': `${offsetX || 0}px`,
        '--offset-y': `${offsetY || 0}px`,
        '--frame-zoom': `${frameZoom || 94}%`,
        '--beat-speed': animationSpeed
    };

    // Estructuras SVG de las figuras para renderizar por GPU
    const getShapeSvg = (type) => {
        switch (type) {
            case 'note':
                return (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16 3H8v11.55C7.4 14.2 6.7 14 6 14c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4V9h6v5.55c-.6-.35-1.3-.55-2-.55-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4V3z" />
                    </svg>
                );
            case 'star':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="var(--shape-border-width)" strokeLinejoin="round">
                        <path d="M50 0 C50 35 65 50 100 50 C65 50 50 65 50 100 C50 65 35 50 0 50 C35 50 50 35 50 0 Z" fill="currentColor" />
                    </svg>
                );
            case 'ball':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="var(--shape-border-width)">
                        <circle cx="50" cy="50" r="45" />
                        <path d="M50 5 L35 30 H65 Z M35 30 L15 50 L35 70 Z M65 30 L85 50 L65 70 Z M35 70 H65 L50 95 Z M50 95 L50 50" />
                    </svg>
                );
            case 'trophy':
                return (
                    <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="var(--shape-border-width)" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M25 15 H75 V45 C75 58 64 68 50 68 C36 68 25 58 25 45 Z" />
                        <path d="M50 68 V85 M35 85 H65" />
                        <path d="M25 25 H10 V40 C10 45 15 50 25 48 Z M75 25 H90 V40 C90 45 85 50 75 48 Z" />
                    </svg>
                );
            case 'heart':
                return (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                );
            case 'circle':
                return (
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="12" cy="12" r="10" />
                    </svg>
                );
            default:
                return null;
        }
    };

    // Generar un set de 12 figuras flotantes con propiedades y trayectorias aleatorias en 3D
    const floatingElements = useMemo(() => {
        if (floatingShape === 'none') return [];
        const shapes = [];
        const typePool = [];

        if (floatingShape === 'notes-stars' || floatingShape === 'all') typePool.push('note', 'star');
        if (floatingShape === 'soccer-balls' || floatingShape === 'all') typePool.push('ball');
        if (floatingShape === 'trophies-stars' || floatingShape === 'all') typePool.push('trophy', 'star');
        if (floatingShape === 'neon-hearts') typePool.push('heart');
        if (floatingShape === 'party-dots') typePool.push('circle');
        if (typePool.length === 0) typePool.push('note', 'star');

        const speedFactor = 120 / (bpm || 120);

        for (let i = 0; i < 12; i++) {
            const shapeType = typePool[i % typePool.length];
            const size = Math.floor(Math.random() * 40) + 30; // 30px to 70px
            const left = Math.floor(Math.random() * 90) + 5; // 5% to 95%
            const duration = (Math.floor(Math.random() * 10) + 12) * speedFactor; // Velocidad proporcional
            const delay = Math.floor(Math.random() * 10); // 0s to 10s
            shapes.push({ id: i, shapeType, size, left, duration, delay });
        }
        return shapes;
    }, [floatingShape, bpm]);

    // Lógica de explosión de partículas sutiles
    const [explodeActive, setExplodeActive] = useState(false);
    const [explosionParticles, setExplosionParticles] = useState([]);
    const prevExplodeTriggerRef = useRef(manualExplodeTrigger);

    useEffect(() => {
        // Disparar destello solo si el trigger actual es mayor al valor anterior (ignorando el render inicial)
        if (manualExplodeTrigger > 0 && manualExplodeTrigger > prevExplodeTriggerRef.current) {
            setExplodeActive(true);
            const timer = setTimeout(() => setExplodeActive(false), 1200);

            // Generar ráfaga radial de notas y estrellas
            const list = [];
            const types = ['note', 'star'];
            const total = 45;
            const screenW = window.innerWidth || 1920;
            const screenH = window.innerHeight || 1080;

            for (let i = 0; i < total; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * 250 + 100; // de 100px a 350px
                const tx = Math.cos(angle) * distance;
                const ty = Math.sin(angle) * distance;
                const size = Math.random() * 25 + 20;
                const delay = (i / total) * 0.45; // Escalonamiento gradual
                const shapeType = types[i % 2];
                const color = shapeType === 'note' ? '#00ffff' : '#ff00ff';

                list.push({
                    id: `${manualExplodeTrigger}-${i}`,
                    x: screenW / 2,
                    y: screenH / 2 - 80,
                    tx,
                    ty,
                    size,
                    delay,
                    color,
                    shapeType
                });
            }
            setExplosionParticles(list);

            prevExplodeTriggerRef.current = manualExplodeTrigger;
            return () => clearTimeout(timer);
        } else {
            // Sincronizar el ref en caso de cambios no detonantes
            prevExplodeTriggerRef.current = manualExplodeTrigger;
        }
    }, [manualExplodeTrigger, fiestaMode]);

    return (
        <div className={overlayClasses} style={cssVariablesStyle}>
            <style>{`
                .dance-overlay-container {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100vw; height: 100vh;
                    overflow: hidden;
                    pointer-events: none;
                    background-color: transparent !important;
                    transition: opacity 1s ease-in-out;
                    z-index: 10;
                }

                .dance-overlay-container.hide-overlay {
                    opacity: 0;
                }

                /* 1. DISCOTECA FOG / HUMO DE FONDO (PREMIUM) */
                .disco-fog {
                    position: absolute;
                    bottom: -20%; left: -20%;
                    width: 140%; height: 70%;
                    background: radial-gradient(ellipse at bottom, var(--fog-color) 0%, transparent 70%);
                    opacity: 0.8;
                    z-index: 1;
                    animation: fog-breath var(--fog-speed) ease-in-out infinite alternate;
                    will-change: opacity, transform;
                }

                @keyframes fog-breath {
                    0% { opacity: 0.6; transform: scale(0.95); }
                    100% { opacity: 0.9; transform: scale(1.05); }
                }

                /* 2. MARCOS CIBERNÉTICOS (CYBER FRAME STYLES) */
                .cyber-frame-visual {
                    position: absolute;
                    top: 50%; left: 50%;
                    width: var(--frame-zoom);
                    height: var(--frame-zoom);
                    transform: translate(-50%, -50%);
                    pointer-events: none;
                    z-index: 15;
                    box-sizing: border-box;
                    will-change: border-color, box-shadow, transform;
                }

                /* Neón Clásico */
                .style-neon-classic .cyber-frame-visual {
                    border: 5px solid var(--neon-primary);
                    border-radius: 25px;
                    box-shadow: 
                        inset 0 0 25px rgba(0, 255, 255, 0.3),
                        0 0 25px rgba(0, 255, 255, 0.4);
                }

                /* Estilo Visor de Estadio */
                .style-stadium-visor .cyber-frame-visual {
                    border: 4px solid var(--neon-secondary);
                    border-radius: 4px;
                    clip-path: polygon(
                        0% 15%, 15% 0%, 85% 0%, 100% 15%,
                        100% 85%, 85% 100%, 15% 100%, 0% 85%
                    );
                    box-shadow: 
                        inset 0 0 35px rgba(255, 0, 255, 0.4),
                        0 0 30px rgba(255, 0, 255, 0.5);
                }

                /* Cyberpunk Glitch */
                .style-cyber-glitch .cyber-frame-visual {
                    border: 5px double var(--neon-primary);
                    border-radius: 12px;
                    box-shadow: 
                        0 0 20px var(--neon-primary),
                        inset 0 0 20px var(--neon-secondary);
                }

                .style-cyber-glitch.fiesta-mode .cyber-frame-visual {
                    animation: frame-glitch-shake 0.15s infinite;
                }

                @keyframes frame-glitch-shake {
                    0% { transform: translate(-50%, -50%) skew(0deg); border-color: var(--neon-primary); }
                    33% { transform: translate(-51%, -49%) skew(0.5deg); border-color: #ffff00; }
                    66% { transform: translate(-49%, -51%) skew(-0.5deg); border-color: var(--neon-secondary); }
                    100% { transform: translate(-50%, -50%) skew(0deg); }
                }

                /* Fiesta Mode General Frame */
                .fiesta-mode .cyber-frame-visual {
                    box-shadow: 
                        inset 0 0 50px var(--neon-secondary),
                        0 0 50px var(--neon-primary) !important;
                    animation: frame-vibe-shake 0.08s infinite !important;
                }

                @keyframes frame-vibe-shake {
                    0%, 100% { transform: translate(-50%, -50%) scale(1.0); }
                    25% { transform: translate(-50.4%, -49.6%) scale(1.003) rotate(0.08deg); }
                    50% { transform: translate(-49.6%, -50.4%) scale(0.997) rotate(-0.08deg); }
                    75% { transform: translate(-50.2%, -50.2%) scale(1.001) rotate(0.04deg); }
                }

                /* 3. LÁSERES DANCE CAM */
                .laser-beam {
                    position: absolute;
                    width: 4px;
                    height: 150vh;
                    top: -15%;
                    transform-origin: top center;
                    opacity: 0.6;
                    pointer-events: none;
                    z-index: 5;
                    will-change: transform, opacity;
                }

                .laser-primary {
                    background: linear-gradient(180deg, #ffffff 0%, var(--neon-primary) 15%, transparent 100%);
                    filter: drop-shadow(0 0 20px var(--neon-primary));
                }

                .laser-secondary {
                    background: linear-gradient(180deg, #ffffff 0%, var(--neon-secondary) 15%, transparent 100%);
                    filter: drop-shadow(0 0 20px var(--neon-secondary));
                }

                .l1 { left: 15%; animation: sweep-l 6s infinite alternate ease-in-out; }
                .l2 { right: 15%; animation: sweep-r 6s infinite alternate ease-in-out; }
                .l3 { left: 50%; opacity: 0.8; animation: sweep-c 4s infinite alternate ease-in-out; }
                .l4 { left: 30%; animation: sweep-l-wide 8s infinite alternate ease-in-out; opacity: 0.4; }
                .l5 { right: 30%; animation: sweep-r-wide 8s infinite alternate ease-in-out; opacity: 0.4; }

                @keyframes sweep-l { 0% { transform: rotate(-35deg); } 100% { transform: rotate(25deg); } }
                @keyframes sweep-r { 0% { transform: rotate(35deg); } 100% { transform: rotate(-25deg); } }
                @keyframes sweep-c { 0% { transform: rotate(-15deg); } 100% { transform: rotate(15deg); } }
                @keyframes sweep-l-wide { 0% { transform: rotate(-45deg); } 100% { transform: rotate(10deg); } }
                @keyframes sweep-r-wide { 0% { transform: rotate(45deg); } 100% { transform: rotate(-10deg); } }

                .fiesta-mode .laser-beam {
                    animation-duration: var(--beat-speed) !important;
                    opacity: 0.9;
                }

                /* 4. FIGURAS FLOTANTES SVG ACELERADAS GPU */
                .floating-shape-item {
                    position: absolute;
                    bottom: -100px;
                    color: var(--shape-color);
                    opacity: var(--shape-opacity);
                    filter: drop-shadow(0 0 var(--shape-glow) var(--shape-glow-rgb));
                    z-index: 6;
                    will-change: transform;
                    animation: float-up-3d linear infinite;
                }

                @keyframes float-up-3d {
                    0% {
                        transform: translate3d(0, 0, 0) rotate(0deg);
                    }
                    100% {
                        transform: translate3d(0, -120vh, 0) rotate(360deg);
                    }
                }

                /* 5. TÍTULO NEÓN PRINCIPAL */
                .dance-title-container {
                    position: absolute;
                    bottom: 6%;
                    left: 50%;
                    transform: translate(-50%, var(--offset-y)) scale(var(--dance-scale));
                    transform-origin: bottom center;
                    text-align: center;
                    z-index: 20;
                    pointer-events: none;
                    will-change: transform, opacity;
                }

                .dance-title-text {
                    font-family: 'Orbitron', sans-serif;
                    font-weight: 900 !important;
                    font-size: var(--title-size);
                    color: #ffffff;
                    margin: 0;
                    line-height: 1;
                    letter-spacing: 12px;
                    text-shadow: 
                        0 0 10px #fff,
                        0 0 25px var(--neon-primary),
                        0 0 50px var(--neon-secondary);
                    animation: neon-pulse-animation 1.5s infinite alternate ease-in-out;
                    will-change: text-shadow, transform;
                }

                @keyframes neon-pulse-animation {
                    from { transform: scale(0.98); text-shadow: 0 0 10px #fff, 0 0 20px var(--neon-primary), 0 0 45px var(--neon-secondary); }
                    to { transform: scale(1.02); text-shadow: 0 0 20px #fff, 0 0 45px var(--neon-primary), 0 0 85px var(--neon-secondary); }
                }

                .fiesta-mode .dance-title-text {
                    color: #ffff00;
                    text-shadow: 0 0 20px #fff, 0 0 50px #ffff00, 0 0 100px var(--neon-secondary);
                    animation: neon-pulse-animation var(--beat-speed) infinite alternate ease-in-out;
                }

                /* 6. LINEAS DE FRECUENCIA (ECUALIZADORES FLEXBOX) */
                .eq-visualizer {
                    position: absolute;
                    bottom: 3.5%;
                    display: flex;
                    align-items: flex-end;
                    gap: 15px;
                    height: 160px;
                    z-index: 12;
                    pointer-events: none;
                    will-change: transform;
                }

                .eq-vis-left { left: 5%; }
                .eq-vis-right { right: 5%; }

                .eq-bar-item {
                    width: 25px;
                    background: linear-gradient(0deg, var(--neon-secondary) 0%, var(--neon-primary) 100%);
                    border-radius: 8px 8px 0 0;
                    box-shadow: 0 0 var(--neon-glow-val) var(--neon-primary);
                    will-change: height;
                }

                /* Animación del ecualizador basada en el tipo de efecto */
                .effect-suave .eq-bar-item {
                    animation: bounce-suave var(--beat-speed) infinite alternate ease-in-out;
                }

                .effect-agresivo .eq-bar-item {
                    animation: bounce-agresivo calc(var(--beat-speed) * 0.5) infinite alternate ease-in;
                }

                .effect-estereo .eq-bar-item {
                    animation: bounce-suave var(--beat-speed) infinite alternate ease-in-out;
                }

                .effect-apagado .eq-bar-item {
                    display: none;
                }

                @keyframes bounce-suave {
                    0% { height: 10%; }
                    100% { height: 50%; }
                }

                @keyframes bounce-agresivo {
                    0% { height: 25%; }
                    100% { height: 95%; }
                }

                /* Desfase de animación manual por barra */
                .eq-bar-item:nth-child(1) { animation-delay: 0.1s; }
                .eq-bar-item:nth-child(2) { animation-delay: 0.3s; background: linear-gradient(0deg, var(--neon-primary) 0%, var(--neon-secondary) 100%); }
                .eq-bar-item:nth-child(3) { animation-delay: 0.0s; }
                .eq-bar-item:nth-child(4) { animation-delay: 0.4s; }
                .eq-bar-item:nth-child(5) { animation-delay: 0.2s; background: linear-gradient(0deg, var(--neon-primary) 0%, var(--neon-secondary) 100%); }

                .effect-estereo .eq-vis-right .eq-bar-item:nth-child(even) {
                    animation-delay: 0.5s;
                    animation-duration: calc(var(--beat-speed) * 1.3);
                }

                /* 7. BAILARINES SILUETEADOS CON MIX BLEND MODE SCREEN */
                .dance-dancers-box {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    overflow: hidden;
                    pointer-events: none;
                    z-index: 14;
                }

                .dancers-character-gif {
                    position: absolute;
                    bottom: 22%;
                    width: 32vh; height: 32vh;
                    mix-blend-mode: screen;
                    filter: drop-shadow(0 0 15px var(--neon-primary));
                    animation: pop-in-dancer 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
                    will-change: transform, opacity;
                }

                @keyframes pop-in-dancer {
                    0% { transform: scale(0) translateY(80px); opacity: 0; }
                    100% { transform: scale(1) translateY(0); opacity: 1; }
                }

                .dancers-character-gif.dancer-exit {
                    animation: pop-out-dancer 0.5s ease-in forwards;
                }

                @keyframes pop-out-dancer {
                    0% { transform: scale(1) translateY(0); opacity: 1; }
                    100% { transform: scale(0) translateY(80px); opacity: 0; }
                }

                /* Explosión manual de destello */
                .explosion-manual-flash {
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    background-color: #ffffff;
                    z-index: 99;
                    opacity: 0;
                    pointer-events: none;
                }

                .explosion-active {
                    animation: manual-flash-effect 0.4s ease-out forwards;
                }

                @keyframes manual-flash-effect {
                    0% { opacity: 0.9; }
                    100% { opacity: 0; }
                }

                .dance-explosion-particle {
                    position: absolute;
                    pointer-events: none;
                    z-index: 50;
                    opacity: 0;
                    transform: translate(-50%, -50%);
                    animation: dance-particle-burst 0.7s cubic-bezier(0.1, 0.8, 0.15, 1) forwards;
                }

                @keyframes dance-particle-burst {
                    0% {
                        opacity: 0;
                        transform: translate(-50%, -50%) translate3d(0, 0, 0) scale(0.2) rotate(0deg);
                    }
                    15% {
                        opacity: 1;
                    }
                    80% {
                        opacity: 1;
                    }
                    100% {
                        opacity: 0;
                        transform: translate(-50%, -50%) translate3d(var(--tx), var(--ty), 0) scale(1.15) rotate(270deg);
                    }
                }
            `}</style>

            {/* Niebla de discoteca pulsante */}
            <div className="disco-fog" />

            {/* Efecto de destello de explosión manual */}
            <div className={`explosion-manual-flash ${explodeActive ? 'explosion-active' : ''}`} />

            {/* Marco cibernético */}
            <div className="cyber-frame-visual" />

            {/* Láseres en vaivén */}
            <div className="laser-beam laser-primary l1" />
            <div className="laser-beam laser-secondary l2" />
            <div className="laser-beam laser-primary l3" />
            <div className="laser-beam laser-secondary l4" />
            <div className="laser-beam laser-primary l5" />

            {/* Figuras Flotantes (Notes/Stars) SVG */}
            {floatingElements.map(el => {
                const isNote = el.shapeType === 'note';
                const shapeColor = isNote ? '#00ffff' : '#ff00ff';
                const shapeGlowColor = isNote ? 'rgba(0, 255, 255, 0.75)' : 'rgba(255, 0, 255, 0.75)';

                return (
                    <div 
                        key={el.id}
                        className="floating-shape-item"
                        style={{
                            width: `${el.size}px`,
                            height: `${el.size}px`,
                            left: `${el.left}%`,
                            color: shapeColor,
                            filter: `drop-shadow(0 0 ${shapeGlow || 15}px ${shapeGlowColor})`,
                            opacity: shapeOpacity ?? 0.8,
                            animationDuration: `${el.duration}s`,
                            animationDelay: `${el.delay}s`
                        }}
                    >
                        {getShapeSvg(el.shapeType)}
                    </div>
                );
            })}

            {/* Partículas de destello explosivo radial */}
            {explodeActive && explosionParticles.map(p => (
                <div 
                    key={p.id}
                    className="dance-explosion-particle"
                    style={{
                        left: `${p.x}px`,
                        top: `${p.y}px`,
                        color: p.color,
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        filter: `drop-shadow(0 0 12px ${p.color})`,
                        '--tx': `${p.tx}px`,
                        '--ty': `${p.ty}px`,
                        animationDelay: `${p.delay}s`
                    }}
                >
                    {getShapeSvg(p.shapeType)}
                </div>
            ))}

            {/* Título Neón */}
            <div className="dance-title-container">
                <h1 className="dance-title-text">{titleText}</h1>
            </div>

            {/* Líneas de Frecuencia (Ecualizador) */}
            <div className={`eq-visualizer eq-vis-left effect-${eqEffect}`}>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
            </div>
            <div className={`eq-visualizer eq-vis-right effect-${eqEffect}`}>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
                <div className="eq-bar-item"></div>
            </div>

            {/* Bailarines silueteados opcionales */}
            {activeDancers.length === 2 && (
                <div className="dance-dancers-box">
                    <img 
                        src={`${process.env.PUBLIC_URL}/img/${activeDancers[0]}`} 
                        alt="Dancer Left" 
                        className="dancers-character-gif" 
                        style={{ left: '6%' }} 
                    />
                    <img 
                        src={`${process.env.PUBLIC_URL}/img/${activeDancers[1]}`} 
                        alt="Dancer Right" 
                        className="dancers-character-gif" 
                        style={{ right: '6%' }} 
                    />
                </div>
            )}
        </div>
    );
};

export { Dance };
export default Dance;
